import { useState } from "react";
import { createLead } from "@/lib/api";
import { useSimulation } from "@/context/SimulationContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, CheckCircle2 } from "lucide-react";

export default function Kontak() {
  const { input, result } = useSimulation();
  const [form, setForm] = useState({ nama: "", whatsapp: "", kota: "", waktu_kontak: "", catatan: "" });
  const [consent, setConsent] = useState(false);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nama || !form.whatsapp) { toast.error("Nama dan WhatsApp wajib"); return; }
    if (!consent) { toast.error("Persetujuan untuk dihubungi diperlukan"); return; }
    setLoading(true);
    try {
      await createLead({ ...form, consent, simulation_snapshot: { input, result } });
      setSent(true);
      toast.success("Permintaan konsultasi tersimpan");
    } catch (e) {
      toast.error("Gagal mengirim: " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="page-kontak">
      <div className="pe-container pt-12 pb-6">
        <div className="pe-eyebrow">Kontak</div>
        <h1 className="mt-2 font-display font-bold text-3xl lg:text-4xl">Mari diskusikan rencana PLTS rumah Bapak/Ibu</h1>
        <p className="mt-3 text-[color:var(--pe-text-muted)] max-w-2xl">Isi formulir, atau sapa kami langsung di WhatsApp. Hasil simulasi akan ikut tersimpan bila tersedia.</p>
      </div>
      <div className="pe-container grid lg:grid-cols-5 gap-6 pb-16">
        <div className="lg:col-span-3">
          <div className="pe-card">
            {sent ? (
              <div className="text-center py-10" data-testid="form-success">
                <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-500" />
                <h2 className="mt-4 font-display font-bold text-2xl">Terima kasih, {form.nama}</h2>
                <p className="mt-2 text-[color:var(--pe-text-muted)]">Tim kami akan menghubungi melalui WhatsApp di waktu yang dipilih.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="konsultasi-form">
                <div>
                  <Label>Nama</Label>
                  <Input className="mt-2" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} data-testid="input-nama" required />
                </div>
                <div>
                  <Label>WhatsApp</Label>
                  <Input className="mt-2" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="08xx…" data-testid="input-whatsapp" required />
                </div>
                <div>
                  <Label>Kota</Label>
                  <Input className="mt-2" value={form.kota} onChange={(e) => setForm({ ...form, kota: e.target.value })} data-testid="input-kota" />
                </div>
                <div>
                  <Label>Waktu kontak pilihan</Label>
                  <Input className="mt-2" value={form.waktu_kontak} onChange={(e) => setForm({ ...form, waktu_kontak: e.target.value })} placeholder="Mis. Sabtu pagi" data-testid="input-waktu" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Catatan</Label>
                  <Textarea className="mt-2" value={form.catatan} onChange={(e) => setForm({ ...form, catatan: e.target.value })} rows={4} data-testid="input-catatan" />
                </div>
                <div className="sm:col-span-2 flex items-start gap-2">
                  <Checkbox checked={consent} onCheckedChange={setConsent} id="consent" data-testid="input-consent" />
                  <label htmlFor="consent" className="text-sm text-[color:var(--pe-text-muted)]">
                    Saya setuju Proper Energy menghubungi saya terkait permintaan konsultasi ini.
                  </label>
                </div>
                <div className="sm:col-span-2">
                  <Button className="pe-btn-primary" type="submit" disabled={loading} data-testid="btn-submit-lead">
                    {loading ? "Mengirim..." : "Kirim permintaan konsultasi"}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
        <div className="lg:col-span-2 space-y-5">
          <div className="pe-card">
            <h3 className="font-display font-semibold">Hubungi langsung</h3>
            <ul className="mt-4 space-y-3 text-sm">
              <li className="flex items-start gap-2"><MessageCircle size={16} className="mt-0.5" /><a href="https://wa.me/6285121341168" target="_blank" rel="noopener noreferrer" className="hover:text-[color:var(--pe-primary)]" data-testid="link-whatsapp">+62 851-2134-1168 (WhatsApp)</a></li>
              <li className="flex items-start gap-2"><Phone size={16} className="mt-0.5" /><span>+62 851-2134-1168</span></li>
              <li className="flex items-start gap-2"><Mail size={16} className="mt-0.5" /><a href="mailto:ejar@properenergy.id" className="hover:text-[color:var(--pe-primary)]">ejar@properenergy.id (konsultasi)</a></li>
              <li className="flex items-start gap-2"><Mail size={16} className="mt-0.5" /><a href="mailto:office@properenergy.id" className="hover:text-[color:var(--pe-primary)]">office@properenergy.id (umum)</a></li>
              <li className="flex items-start gap-2"><MapPin size={16} className="mt-0.5" /><span>Jl. Bintaro Raya Selatan No.8, Kebayoran Lama, Jakarta Selatan 12240</span></li>
            </ul>
          </div>
          <div className="pe-card" style={{ background: "var(--pe-surface)" }}>
            <p className="text-sm text-[color:var(--pe-text-muted)]">
              Layanan prioritas Jabodetabek. Cakupan dan ketentuan survei dapat berbeda untuk wilayah lain — tim kami akan konfirmasi saat menghubungi.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
